import React, { useEffect, lazy, Suspense } from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ImageHoverPreview from "./component/ImageHoverPreview";
import ProtectedRoute from "./component/ProtectedRoute";
import Login from "./Pages/Login";
import { routes } from "./config/routeRegistry";
import { useBranding } from "./context/BrandingContext";
import { usePermissions } from "./context/PermissionContext";

const NoAccess = lazy(() => import("./Pages/NoAccess"));

/**
 * Routing is table-driven. Every entry in routeRegistry declares the module it
 * belongs to and <ProtectedRoute> resolves that against the permission set the
 * server issued for this user.
 *
 * The previous version nested routes inside `user?.role === "..."` blocks with
 * per-user id exceptions; adding a role meant editing this file. It no longer
 * does - access is configured in System > Role Permissions.
 */

const PageLoader = () => {
  const { accentColor } = useBranding();
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#F8FAFC]">
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-10 h-10 border-4 border-slate-200 rounded-full animate-spin"
          style={{ borderTopColor: accentColor }}
        />
        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
          Loading…
        </p>
      </div>
    </div>
  );
};

/** Sends a signed-in user to their role's landing route, or the first menu item. */
const LandingRedirect = () => {
  const user = useSelector((state) => state.auth.user);
  const { menuTree, loading } = usePermissions();

  if (loading) return <PageLoader />;

  const firstFromMenu =
    menuTree.find((n) => n.routePath)?.routePath ||
    menuTree.flatMap((n) => n.children || []).find((c) => c.routePath)?.routePath;

  // Same guard as the login redirect: honour the role's landing route only if
  // the user can actually open it, otherwise fall through to their first menu
  // item. A revoked landing page must not become a dead end.
  const reachable = new Set(
    menuTree
      .flatMap((n) => [n, ...(n.children || [])])
      .map((n) => n.routePath)
      .filter(Boolean)
  );

  const target =
    user?.landingRoute && reachable.has(user.landingRoute)
      ? user.landingRoute
      : firstFromMenu;

  return <Navigate to={target || "/no-access"} replace />;
};

const App = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      // If a modal is open, let it handle Escape rather than navigating back.
      const modals = document.querySelectorAll(
        '[role="dialog"], [aria-modal="true"], .z-\\[1000\\], .z-\\[9999\\]'
      );
      for (let i = 0; i < modals.length; i++) {
        const style = window.getComputedStyle(modals[i]);
        if (style.display !== "none" && style.visibility !== "hidden") return;
      }

      const activeEl = document.activeElement;
      const isInputActive =
        activeEl &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.tagName === "SELECT" ||
          activeEl.isContentEditable);

      if (!isInputActive) {
        event.preventDefault();
        navigate(-1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  return (
    <>
      <ToastContainer position="top-center" reverseOrder={true} />
      <ImageHoverPreview />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/no-access" element={<NoAccess />} />

          {routes.map(({ path, component: Component, module, permission }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute module={module} permission={permission || "VIEW"}>
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}

          {/* Anything else: signed-in users land on their own start page,
              everyone else goes to login. */}
          <Route
            path="*"
            element={isLoggedIn ? <LandingRedirect /> : <Navigate to="/" replace />}
          />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;
